import { Box, Button, IconButton, TextField } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import RuleForm from "./RuleForm";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import StyledDataGrid from "styles/styledDataGrid";
import Api from "api";
import { useDispatch } from "react-redux";
import { setLoading, setDialog } from "slices/appSlice";
import { formErrorStrfromObj } from "utils/shared";

function RuleTable({
  inputs,
  outputs,
  showFieldSelection,
  onSubmit,
  rules,
  ruleColumnMetaMap,
  editScreen = false,
  ruleSetKey,
  fetchRuleSet,
}) {
  const [rows, setRows] = useState([]);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editRowId, setEditRowId] = useState(null);
  const [editRowData, setEditRowData] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Use a single paginationModel state similar to the MUI example.
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  // When rules change, map them to grid rows and reset pagination.
  useEffect(() => {
    if (rules) {
      setRows(rules);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }, [rules]);

  // Compute the rows to display on the current page.
  const paginatedRows = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize;
    return rows.slice(start, start + paginationModel.pageSize);
  }, [rows, paginationModel]);

  const handleEdit = (id) => {
    const row = rules.find((r) => r.id === id); // <- use full rules instead of paginatedRows
    if (!row) {
      console.warn(`Row with id ${id} not found in rules`);
      return;
    }
    setEditRowId(id);
    setEditRowData({ ...row });
  };

  const handleCancel = () => {
    setEditRowId(null);
    setEditRowData({});
  };

  const handleSave = async (id) => {
    dispatch(setLoading(true));
    const dto = buildRuleDto(editRowData);
    try {
      const response = await Api.ruleConfigProcess.updateRule(
        editRowData.ruleKey,
        dto
      );
      if (response.status === 200) {
        await fetchRuleSet(ruleSetKey);
        setEditRowData({});
        setEditRowId(null);
        dispatch(
          setDialog({
            message: "Rule saved successfully",
            title: "Success",
          })
        );
      }
    } catch (err) {
      dispatch(
        setDialog({ message: formErrorStrfromObj(err), title: err.message })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const deleteRow = (id) => {
    setRows((oldRows) => oldRows.filter((r) => r.id !== id));
  };

  // When adding a new rule via the form, transform the rule (coming in unprefixed) to include the prefixed keys.
  const addRule = (rule) => {
    const newRule = { ...rule, id: rows.length };
    // For each input field, add the prefixed key if the unprefixed key exists.
    inputs.forEach((f) => {
      const key = String(f.ruleConfigFieldKey);
      if (newRule[key] !== undefined) {
        newRule[`input_${key}`] = newRule[key];
        delete newRule[key];
      }
    });
    // For each output field, do the same.
    outputs.forEach((f) => {
      const key = String(f.ruleConfigFieldKey);
      if (newRule[key] !== undefined) {
        newRule[`output_${key}`] = newRule[key];
        delete newRule[key];
      }
    });

    if (editScreen) {
      createNewRule(ruleSetKey, newRule);
    } else {
      setRows([...rows, newRule]);
    }
    setShowRuleForm(false);
  };

  const createNewRule = async (ruleSetKey, newRule) => {
    try {
      const dto = buildRuleDto(newRule);
      const response = await Api.ruleConfigProcess.createRule(ruleSetKey, dto);
      if (response.status === 200) {
        setRows([...rows, newRule]);
        dispatch(
          setDialog({
            message: "Rule created successfully",
            title: "Success",
          })
        );
      }
    } catch (err) {
      dispatch(
        setDialog({ message: formErrorStrfromObj(err), title: err.message })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const deleteExistingRule = async (rule) => {
    try {
      const response = await Api.ruleConfigProcess.deleteRule(rule.ruleKey);
      if (response.status === 200) {
        setRows((oldRows) => oldRows.filter((r) => r.ruleKey !== rule.ruleKey));
        dispatch(
          setDialog({
            message: "Rule deleted successfully",
            title: "Success",
          })
        );
      }
    } catch (err) {
      dispatch(
        setDialog({ message: formErrorStrfromObj(err), title: err.message })
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleFieldSelection = () => {
    if (confirm("This will erase all rules. Press Ok to proceed.")) {
      showFieldSelection();
    }
  };

  // Generate the DTO by stripping out the prefixes.
  const geneRateRuleDTOFromRows = (rows) => {
    const rulesList = [];
    rows.forEach((r) => {
      const rule = {
        ruleConfigRuleName: r.name,
        priority: r.priority || 100,
      };
      const ruleColumns = [];
      Object.entries(r).forEach(([key, fieldValue]) => {
        if (key.startsWith("input_")) {
          ruleColumns.push({
            fieldValue,
            ruleFieldKey: key.replace("input_", ""),
            columnType: "INPUT",
          });
        } else if (key.startsWith("output_")) {
          ruleColumns.push({
            fieldValue,
            ruleFieldKey: key.replace("output_", ""),
            columnType: "OUTPUT",
          });
        }
      });
      rule.ruleColumns = ruleColumns;
      rulesList.push(rule);
    });
    return rulesList;
  };

  const handleSubmit = () => {
    const rulesList = geneRateRuleDTOFromRows(rows);
    onSubmit(rulesList);
  };

  const buildRuleDto = (rowData) => {
    const ruleKey = rowData.ruleKey;
    const ruleName = rowData.name;
    const priority = rowData.priority;

    const ruleColumns = Object.entries(rowData)
      .filter(([key]) => key.startsWith("input_") || key.startsWith("output_"))
      .map(([key, value]) => {
        const [typePrefix, fieldKeyStr] = key.split("_");
        const columnType = typePrefix.toUpperCase(); // "INPUT" or "OUTPUT"
        const ruleFieldKey = Number(fieldKeyStr);
        const metaKey = `${ruleKey}_${columnType}_${ruleFieldKey}`;
        const meta = ruleColumnMetaMap.get(metaKey) || {};

        return {
          ruleConfigRuleInputKey: meta.ruleConfigRuleInputKey ?? null,
          ruleFieldKey,
          fieldValue: value,
          columnType,
          operator: meta.operator ?? "==",
        };
      });

    return {
      ruleConfigRuleKey: ruleKey,
      ruleConfigRuleName: ruleName,
      priority: Number(priority),
      ruleColumns,
    };
  };

  // Column grouping model using prefixed field names for uniqueness.
  const columnGroupingModel = [
    {
      groupId: "input",
      headerAlign: "center",
      description: "Input",
      children: inputs.map((f) => ({ field: `input_${f.ruleConfigFieldKey}` })),
    },
    {
      groupId: "output",
      headerAlign: "center",
      description: "Output",
      children: outputs.map((f) => ({
        field: `output_${f.ruleConfigFieldKey}`,
      })),
    },
  ];

  // Define grid columns with prefixed keys.
  const columns = useMemo(() => {
    return [
      {
        field: "name",
        flex: 1,
      },
      ...inputs.map((f) => ({
        field: `input_${f.ruleConfigFieldKey}`,
        flex: 1,
        headerName: f.ruleConfigFieldName,
        renderCell: (params) =>
          editRowId === params.row.id ? (
            <TextField
              variant="standard"
              fullWidth
              value={editRowData[`input_${f.ruleConfigFieldKey}`] || ""}
              onChange={(e) =>
                setEditRowData({
                  ...editRowData,
                  [`input_${f.ruleConfigFieldKey}`]: e.target.value,
                })
              }
            />
          ) : (
            params.value
          ),
      })),
      ...outputs.map((f) => ({
        field: `output_${f.ruleConfigFieldKey}`,
        flex: 1,
        headerName: f.ruleConfigFieldName,
        renderCell: (params) =>
          editRowId === params.row.id ? (
            <TextField
              variant="standard"
              fullWidth
              value={editRowData[`output_${f.ruleConfigFieldKey}`] || ""}
              onChange={(e) =>
                setEditRowData({
                  ...editRowData,
                  [`output_${f.ruleConfigFieldKey}`]: e.target.value,
                })
              }
            />
          ) : (
            params.value
          ),
      })),
      {
        field: "actions",
        flex: 1,
        sortable: false,
        headerName: "Actions",
        filterable: false,
        renderCell: (params) => {
          const isInEditMode = editRowId === params.row.id;
          if (!editScreen) {
            return (
              <IconButton size="small" onClick={() => deleteRow(params.row.id)}>
                <DeleteIcon />
              </IconButton>
            );
          }
          return isInEditMode ? (
            <>
              <IconButton
                size="small"
                onClick={() => handleSave(params.row.id)}
              >
                <SaveIcon />
              </IconButton>
              <IconButton size="small" onClick={handleCancel}>
                <CancelIcon />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                size="small"
                onClick={() => handleEdit(params.row.id)}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => deleteExistingRule(params.row)}
              >
                <DeleteIcon />
              </IconButton>
            </>
          );
        },
      },
    ];
  }, [inputs, outputs, editRowId, editRowData]);

  return (
    <Box
      sx={{
        flexGrow: 1,
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {showRuleForm && (
        <RuleForm
          inputs={inputs}
          outputs={outputs}
          onCancel={() => setShowRuleForm(false)}
          addRule={addRule}
          editScreen
        />
      )}
      {inputs.length && outputs.length ? (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box sx={{ mb: 1 }}>
            <Button onClick={() => setShowRuleForm(true)}>Add Rule</Button>
            {!editScreen && (
              <Button onClick={handleFieldSelection}>
                Edit Rule Structure
              </Button>
            )}
          </Box>
          {/* Grid container that fills remaining space and scrolls internally */}
          <Box sx={{ flexGrow: 1, minHeight: 0, overflow: "auto" }}>
            <StyledDataGrid
              columns={columns}
              rows={paginatedRows}
              rowHeight={32}
              columnGroupingModel={columnGroupingModel}
              getRowId={(row) => row.id}
              sx={{ height: "100%", width: "100%" }}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              rowCount={rows.length}
              onPaginationModelChange={setPaginationModel}
            />
          </Box>
          <Box sx={{ mt: 1 }}>
            <Button onClick={() => navigate(-1)}>
              {editScreen ? "Go Back" : "Cancel"}
            </Button>
            {!editScreen && <Button onClick={handleSubmit}>Submit</Button>}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            height: "100%",
          }}
        >
          <Box>No rules available under this RuleSet.</Box>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </Box>
      )}
    </Box>
  );
}

export default RuleTable;
